import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useLanguage } from "@/contexts/LanguageContext";
import { formatCurrency } from "@/lib/currencies";
import { CurrencySelect } from "@/components/CurrencySelect";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogFooter 
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { toast } from "sonner";
import { 
  FileCheck, 
  Check, 
  X,
  Eye,
  CalendarIcon,
  Loader2,
  FileText,
  Clock,
  CheckCircle2,
  Send,
  Download
} from "lucide-react";
import { format, parseISO } from "date-fns";
import { de } from "date-fns/locale";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_COLORS = {
  draft: { color: "bg-amber-500", icon: FileText },
  reviewed: { color: "bg-blue-500", icon: CheckCircle2 },
  booked: { color: "bg-green-500", icon: Check },
};


export default function ReviewPage() {
  const [expenses, setExpenses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [selectedExpense, setSelectedExpense] = useState(null);
  const [formData, setFormData] = useState({});
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [saving, setSaving] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const { t } = useLanguage();

  const fetchData = useCallback(async () => {
    try {
      const [expensesRes, categoriesRes] = await Promise.all([
        axios.get(`${API}/expenses/pending-review`),
        axios.get(`${API}/categories`),
      ]);
      setExpenses(expensesRes.data);
      setCategories(categoriesRes.data);
    } catch (error) {
      toast.error("Fehler beim Laden der Daten");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleOpenReview = (expense) => {
    setSelectedExpense(expense);
    setFormData({
      title: expense.title || "",
      amount: expense.amount?.toString() || "",
      currency: expense.currency || "EUR",
      category_id: expense.category_id || "",
      invoice_number: expense.invoice_number || "",
      vendor: expense.vendor || "",
      description: expense.description || "",
      expense_type: expense.expense_type || "variable",
      review_notes: "",
    });
    setSelectedDate(expense.date ? parseISO(expense.date) : new Date());
    setReviewDialogOpen(true);
  };

  const handleSaveAndApprove = async (newStatus) => {
    setSaving(true);
    try {
      // First update the expense data (preserve attachment)
      await axios.put(`${API}/expenses/${selectedExpense.id}`, {
        ...formData,
        amount: parseFloat(formData.amount),
        date: format(selectedDate, "yyyy-MM-dd"),
        status: selectedExpense.status,
        attachment_data: selectedExpense.attachment_data || null,
        attachment_name: selectedExpense.attachment_name || null,
        payment_status: selectedExpense.payment_status || "open",
      });

      // Then update status
      await axios.put(`${API}/expenses/${selectedExpense.id}/status`, {
        status: newStatus,
        review_notes: formData.review_notes,
      });

      toast.success("Ausgabe geprüft und gebucht");
      
      setReviewDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error("Fehler beim Speichern");
    } finally {
      setSaving(false);
    }
  };

  const handleReject = async () => {
    setSaving(true);
    try {
      // Delete the expense if rejected
      await axios.delete(`${API}/expenses/${selectedExpense.id}`);
      toast.success("Ausgabe gelöscht");
      setReviewDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error("Fehler beim Löschen");
    } finally {
      setSaving(false);
    }
  };

  // Show all expenses pending review (both "draft" and "pending_review")
  const pendingExpenses = expenses.filter(e => e.status === "draft" || e.status === "pending_review");

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-10 bg-muted rounded animate-pulse w-48" />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[...Array(4)].map((_, i) => (
            <Card key={i} className="animate-pulse">
              <CardContent className="p-6">
                <div className="h-24 bg-muted rounded" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="review-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl md:text-3xl font-bold font-[Manrope] tracking-tight">{t("review.title")}</h1>
        <p className="text-muted-foreground mt-1 text-sm md:text-base">
          {t("review.subtitle")}
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 gap-4">
        <Card className="card-hover">
          <CardContent className="p-4 md:p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs md:text-sm font-medium text-muted-foreground">{t("review.toReview")}</p>
                <p className="text-2xl md:text-3xl font-bold mt-1">{pendingExpenses.length}</p>
              </div>
              <div className="h-10 w-10 rounded-full bg-amber-500/10 flex items-center justify-center">
                <FileText className="h-5 w-5 text-amber-500" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="card-hover">
          <CardContent className="p-4 md:p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs md:text-sm font-medium text-muted-foreground">{t("review.totalOpen")}</p>
                <p className="text-2xl md:text-3xl font-bold mt-1">{pendingExpenses.length}</p>
              </div>
              <div className="h-10 w-10 rounded-full bg-[#E31E24]/10 flex items-center justify-center">
                <FileCheck className="h-5 w-5 text-[#E31E24]" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Expenses to Review */}
      {pendingExpenses.length > 0 ? (
        <div>
          <h2 className="text-lg font-semibold font-[Manrope] mb-4 flex items-center gap-2">
            <FileText className="h-5 w-5 text-amber-500" />
            {t("review.pendingInvoices")} ({pendingExpenses.length})
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {pendingExpenses.map((expense) => (
              <Card key={expense.id} className="card-hover border-amber-500/30" data-testid={`review-card-${expense.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-2">
                        <Badge className={`${expense.status === "pending_review" ? "bg-blue-500" : "bg-amber-500"} text-white`}>
                          {expense.status === "pending_review" ? (
                            <>
                              <Clock className="h-3 w-3 mr-1" />
                              {t("review.pendingReview")}
                            </>
                          ) : (
                            <>
                              <FileText className="h-3 w-3 mr-1" />
                              {t("review.draft")}
                            </>
                          )}
                        </Badge>
                      </div>
                      <h3 className="font-semibold truncate">{expense.title || t("review.noInvoices")}</h3>
                      <p className="text-sm text-muted-foreground mt-1">
                        {expense.vendor || t("expenses.vendor")} • {expense.date ? format(parseISO(expense.date), "dd.MM.yyyy") : t("expenses.date")}
                      </p>
                      <p className="text-lg font-bold font-[JetBrains_Mono] mt-2">
                        {formatCurrency(expense.amount || 0, expense.currency)}
                      </p>
                    </div>
                    <div className="flex flex-col gap-2 ml-4">
                      {expense.attachment_data && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setSelectedExpense(expense);
                            setPreviewOpen(true);
                          }}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                      )}
                      <Button
                        size="sm"
                        className="bg-[#E31E24] hover:bg-[#c91a1f]"
                        onClick={() => handleOpenReview(expense)}
                        data-testid={`review-btn-${expense.id}`}
                      >
                        {t("review.approve")}
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      ) : (
        <Card>
          <CardContent className="p-8 text-center text-muted-foreground">
            <CheckCircle2 className="h-12 w-12 mx-auto mb-4 opacity-50 text-green-500" />
            <p className="font-medium">{t("review.allReviewed")}</p>
            <p className="text-sm mt-1">{t("review.noInvoices")}</p>
          </CardContent>
        </Card>
      )}

      {/* Review Dialog */}
      <Dialog open={reviewDialogOpen} onOpenChange={setReviewDialogOpen}>
        <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-[Manrope]">{t("review.approve")}</DialogTitle>
          </DialogHeader>
          
          {selectedExpense && (
            <div className="space-y-4 mt-4">
              {/* Attachment Preview */}
              {selectedExpense.attachment_data && (
                <div className="border rounded-lg p-2 bg-muted/50">
                  <p className="text-sm font-medium mb-2">{t("review.preview")}:</p>
                  {selectedExpense.attachment_data.startsWith('data:image') ? (
                    <div className="text-center">
                      <img 
                        src={selectedExpense.attachment_data} 
                        alt="Beleg" 
                        className="max-h-48 mx-auto rounded cursor-pointer hover:opacity-90"
                        onClick={() => {
                          const newWindow = window.open();
                          newWindow.document.write(`<img src="${selectedExpense.attachment_data}" style="max-width:100%"/>`);
                        }}
                      />
                      <p className="text-xs text-muted-foreground mt-2">{t("review.preview")}</p>
                    </div>
                  ) : (
                    <div className="text-center py-4">
                      <FileText className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
                      <p className="text-sm font-medium">{selectedExpense.attachment_name || "PDF Dokument"}</p>
                      <Button
                        variant="outline"
                        size="sm"
                        className="mt-3"
                        onClick={() => {
                          // Open PDF in new tab
                          const newWindow = window.open();
                          newWindow.document.write(`
                            <html>
                              <head><title>${selectedExpense.attachment_name || 'Beleg'}</title></head>
                              <body style="margin:0;padding:0;">
                                <embed src="${selectedExpense.attachment_data}" type="application/pdf" width="100%" height="100%" style="position:absolute;top:0;left:0;right:0;bottom:0;"/>
                              </body>
                            </html>
                          `);
                        }}
                      >
                        <Eye className="h-4 w-4 mr-2" />
                        {t("review.openPdf")}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="mt-2 ml-2"
                        onClick={() => {
                          // Download PDF
                          const link = document.createElement('a');
                          link.href = selectedExpense.attachment_data;
                          link.download = selectedExpense.attachment_name || 'beleg.pdf';
                          link.click();
                        }}
                      >
                        <Download className="h-4 w-4 mr-2" />
                        {t("review.download")}
                      </Button>
                    </div>
                  )}
                </div>
              )}

              {/* Form Fields */}
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Label htmlFor="title">Titel *</Label>
                  <Input
                    id="title"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder="Beschreibung der Ausgabe"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="amount">Betrag *</Label>
                  <div className="flex gap-2">
                    <Input
                      id="amount"
                      type="number"
                      step="0.01"
                      value={formData.amount}
                      onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                      placeholder="0.00"
                      className="flex-1"
                    />
                    <CurrencySelect
                      value={formData.currency}
                      onValueChange={(value) => setFormData({ ...formData, currency: value })}
                    />
                  </div>
                </div>
                <div>
                  <Label>Datum</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="w-full justify-start">
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {format(selectedDate, "dd.MM.yyyy", { locale: de })}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0">
                      <Calendar
                        mode="single"
                        selected={selectedDate}
                        onSelect={(date) => date && setSelectedDate(date)}
                        locale={de}
                      />
                    </PopoverContent>
                  </Popover>
                </div>
                <div>
                  <Label htmlFor="vendor">Anbieter</Label>
                  <Input
                    id="vendor"
                    value={formData.vendor}
                    onChange={(e) => setFormData({ ...formData, vendor: e.target.value })}
                    placeholder="Firmenname"
                  />
                </div>
                <div>
                  <Label htmlFor="invoice_number">Rechnungsnummer</Label>
                  <Input
                    id="invoice_number"
                    value={formData.invoice_number}
                    onChange={(e) => setFormData({ ...formData, invoice_number: e.target.value })}
                    placeholder="z.B. RE-2024-001"
                  />
                </div>
                <div>
                  <Label htmlFor="category">Kategorie</Label>
                  <Select
                    value={formData.category_id}
                    onValueChange={(value) => setFormData({ ...formData, category_id: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Wählen..." />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map((cat) => (
                        <SelectItem key={cat.id} value={cat.id}>
                          {cat.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="type">Typ</Label>
                  <Select
                    value={formData.expense_type}
                    onValueChange={(value) => setFormData({ ...formData, expense_type: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="variable">Variabel</SelectItem>
                      <SelectItem value="fix">Fix</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-2">
                  <Label htmlFor="description">Beschreibung</Label>
                  <Textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Zusätzliche Details..."
                    rows={2}
                  />
                </div>
                <div className="col-span-2">
                  <Label htmlFor="review_notes">Prüfnotizen</Label>
                  <Textarea
                    id="review_notes"
                    value={formData.review_notes}
                    onChange={(e) => setFormData({ ...formData, review_notes: e.target.value })}
                    placeholder="Notizen zur Prüfung (optional, bei Ablehnung erforderlich)"
                    rows={2}
                  />
                </div>
              </div>

              <DialogFooter className="flex-col sm:flex-row gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setReviewDialogOpen(false)}
                >
                  Abbrechen
                </Button>
                <Button
                  type="button"
                  variant="destructive"
                  onClick={handleReject}
                  disabled={saving}
                  data-testid="reject-btn"
                >
                  <X className="h-4 w-4 mr-1" />
                  Ablehnen
                </Button>
                <Button
                  type="button"
                  onClick={() => handleSaveAndApprove("booked")}
                  disabled={saving}
                  className="bg-green-500 hover:bg-green-600"
                  data-testid="approve-btn"
                >
                  {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Check className="h-4 w-4 mr-1" />}
                  Geprüft
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Image/PDF Preview Dialog */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="sm:max-w-[900px] max-h-[90vh]">
          <DialogHeader>
            <DialogTitle>Beleg-Vorschau: {selectedExpense?.attachment_name || "Dokument"}</DialogTitle>
          </DialogHeader>
          {selectedExpense?.attachment_data && (
            <div className="h-[70vh] overflow-hidden flex flex-col">
              {selectedExpense.attachment_data.startsWith('data:image') ? (
                <img 
                  src={selectedExpense.attachment_data} 
                  alt="Beleg" 
                  className="w-full h-full object-contain rounded"
                />
              ) : selectedExpense.attachment_data.startsWith('data:application/pdf') ? (
                <div className="flex flex-col h-full">
                  {/* PDF embed using object tag for better browser support */}
                  <object
                    data={selectedExpense.attachment_data}
                    type="application/pdf"
                    className="w-full flex-1 rounded border"
                  >
                    {/* Fallback if object doesn't work */}
                    <div className="flex flex-col items-center justify-center h-full text-center text-muted-foreground p-8">
                      <FileText className="h-16 w-16 mb-4" />
                      <p className="font-medium mb-2">{selectedExpense.attachment_name || "PDF Dokument"}</p>
                      <p className="text-sm mb-4">Die PDF-Vorschau wird von Ihrem Browser nicht unterstützt.</p>
                    </div>
                  </object>
                  {/* Action buttons */}
                  <div className="flex gap-2 justify-center mt-4 pt-4 border-t">
                    <Button
                      variant="default"
                      className="bg-[#E31E24] hover:bg-[#c91a1f]"
                      onClick={() => {
                        // Create blob URL for better PDF viewing
                        const byteCharacters = atob(selectedExpense.attachment_data.split(',')[1]);
                        const byteNumbers = new Array(byteCharacters.length);
                        for (let i = 0; i < byteCharacters.length; i++) {
                          byteNumbers[i] = byteCharacters.charCodeAt(i);
                        }
                        const byteArray = new Uint8Array(byteNumbers);
                        const blob = new Blob([byteArray], { type: 'application/pdf' });
                        const blobUrl = URL.createObjectURL(blob);
                        window.open(blobUrl, '_blank');
                      }}
                    >
                      <Eye className="h-4 w-4 mr-2" />
                      PDF in neuem Tab öffnen
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => {
                        const link = document.createElement('a');
                        link.href = selectedExpense.attachment_data;
                        link.download = selectedExpense.attachment_name || 'beleg.pdf';
                        link.click();
                      }}
                    >
                      <Download className="h-4 w-4 mr-2" />
                      Herunterladen
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="text-center text-muted-foreground py-8">
                  <FileText className="h-16 w-16 mx-auto mb-4" />
                  <p>{selectedExpense.attachment_name || "Dokument"}</p>
                  <div className="flex gap-2 justify-center mt-4">
                    <Button
                      variant="outline"
                      onClick={() => {
                        const link = document.createElement('a');
                        link.href = selectedExpense.attachment_data;
                        link.download = selectedExpense.attachment_name || 'dokument';
                        link.click();
                      }}
                    >
                      <Download className="h-4 w-4 mr-2" />
                      Herunterladen
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
